import { z } from 'zod'

/** Analysis upload constraints (EMS-9883): .xlsx/.xlsm only, max 50 MB. */
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024

export const OEP_UPLOAD_CONTENT_TYPES: Readonly<Record<string, string>> = {
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xlsm': 'application/vnd.ms-excel.sheet.macroEnabled.12',
}

const ACCEPTED_EXTENSIONS = Object.keys(OEP_UPLOAD_CONTENT_TYPES)

/** Return the file's lowercased extension (with leading dot), or null if it has none. */
export function resolveOepUploadExtension(filename: string): string | null {
  const match = /\.[^./\\]+$/.exec(filename.toLowerCase())
  return match !== null && match[0] in OEP_UPLOAD_CONTENT_TYPES
    ? match[0]
    : null
}

/**
 * Zod schema validating a selected analysis-document File before it is uploaded.
 * Refinements are ordered so the first failure surfaced matches the UI copy the
 * reviewer expects (extension before size). z.file() is Zod 4 only; the project
 * is on Zod 3, so we validate the File instance directly.
 */
export const oepUploadFileSchema = z
  .instanceof(File, { message: 'Please choose a file to upload.' })
  .refine((file) => resolveOepUploadExtension(file.name) !== null, {
    message: `Only ${ACCEPTED_EXTENSIONS.join(', ')} files are accepted.`,
  })
  .refine((file) => file.size <= MAX_FILE_SIZE_BYTES, {
    message: 'File must be 50 MB or smaller.',
  })

/** Validate a file and return the first error message, or null when it passes. */
export function validateOepUploadFile(file: File): string | null {
  const result = oepUploadFileSchema.safeParse(file)
  return result.success ? null : (result.error.issues[0]?.message ?? null)
}
